import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState, AppDispatch} from 'store/store';
import {
  setDocumentTemplateProgram,
  setIsEditing,
} from './documentTemplateProgramSlice';
import {setActiveTab, PROGRAM_TABS} from 'features/programs/programsSlice';
import {DocumentTemplateProgramThunks} from './documentTemplateProgramThunks';
import {DocumentTemplateProgram} from 'types/documentTemplateProgram';
import DotsMenu from 'components/DotsMenu';
import BlueButton from 'components/BlueButton';
import Table from 'components/table/Table';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import TableCell from 'components/table/TableCell';
import PlusIcon from 'assets/images/icons/PlusIcon';
import {SearchType} from 'types/search';
import usePermissions from 'hooks/usePermissions';
import {useNavigate, useParams} from 'react-router-dom';

interface DocumentTemplateProgramTableProps {
  openDrawer: () => void;
}

const DocumentTemplateProgramTable: React.FC<
  DocumentTemplateProgramTableProps
> = ({openDrawer}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {checkPermission} = usePermissions();
  const {programId} = useParams<{programId: string}>();
  const pagy = useSelector(
    (state: RootState) => state.documentTemplateProgram.pagy,
  );

  const documentTemplatePrograms = useSelector(
    (state: RootState) =>
      state.documentTemplateProgram.documentTemplatePrograms,
  );

  const loading = useSelector(
    (state: RootState) => state.documentTemplateProgram.status === 'loading',
  );

  const fetchData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    search.where.program_id = {
      operator: 'in',
      valueArray: [null, programId],
    };

    await dispatch(
      DocumentTemplateProgramThunks.index({
        page,
        search,
        sortField,
        sortDirection,
        rowsPerPage,
      }),
    );
  };

  const handleEdit = (id: string) => {
    dispatch(setIsEditing(true));
    openDrawer();
    dispatch(DocumentTemplateProgramThunks.show(id));
  };

  const handleOpenNewDocumentTemplateProgram = () => {
    dispatch(setDocumentTemplateProgram(null));
    dispatch(setIsEditing(true));
    openDrawer();
  };

  const handleDelete = async (id: string) => {
    if (
      window.confirm('Are you sure you want to delete this report template?')
    ) {
      dispatch(DocumentTemplateProgramThunks.delete(id.toString()) as any);
    }
  };

  const handleTemplateNameClick = (documentTemplateId: number) => {
    navigate(`/document-templates/${documentTemplateId}`);
  };

  interface VisibleColumnsType {
    name: boolean;
    tabName: boolean;
    type: boolean;
    lastModified: boolean;
  }

  const [visibleColumns, setVisibleColumns] =
    React.useState<VisibleColumnsType>({
      name: true,
      tabName: true,
      type: true,
      lastModified: true,
    });

  const columnOptions = [
    {label: 'Name', value: 'name', isVisible: visibleColumns.name},
    {label: 'Tab Name', value: 'tabName', isVisible: visibleColumns.tabName},
    {label: 'Type', value: 'type', isVisible: visibleColumns.type},
    {
      label: 'Last Modified',
      value: 'lastModified',
      isVisible: visibleColumns.lastModified,
    },
  ];

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  return (
    <>
      <Table
        fetchData={fetchData}
        pagy={pagy}
        pageable={true}
        tabs={PROGRAM_TABS}
        activeTab={'Report Templates'}
        setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
        onColumnToggle={handleColumnToggle}
        searchByOptions={[
          {label: 'Name', value: 'document_template.name', operator: 'like'},
          {label: 'Tab Name', value: 'tab_name', operator: 'like'},
          {label: 'Description', value: 'description', operator: 'like'},
        ]}
        recordsName={'Report Templates'}
        columnOptions={columnOptions}
        searchable={true}
        loading={loading}
        columns={true}
        blueButton={
          checkPermission('create', 'DocumentTemplateProgram') ? (
            <BlueButton
              onClick={handleOpenNewDocumentTemplateProgram}
              label=""
              icon={<PlusIcon />}
            />
          ) : null
        }>
        <thead>
          <tr>
            {visibleColumns.name && <TableHeader label="Name" sortBy="name" />}
            {visibleColumns.tabName && (
              <TableHeader label="Tab Name" sortBy="tab_name" />
            )}
            {visibleColumns.type && <TableHeader label="Type" />}
            {visibleColumns.lastModified && (
              <TableHeader label="Last Modified" sortBy="updated_at" />
            )}
            <TableHeader label="" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
          {documentTemplatePrograms?.map((item: DocumentTemplateProgram) => (
            <TableRow key={item.id}>
              {visibleColumns.name && (
                <TableCell>
                  <div className="text-sm text-slate-900 dark:text-neutral-100">
                    <span
                      className="cursor-pointer hover:text-cyan-600 hover:underline"
                      onClick={() =>
                        handleTemplateNameClick(item.documentTemplateId)
                      }>
                      {item.name}
                    </span>
                  </div>
                </TableCell>
              )}
              {visibleColumns.tabName && (
                <TableCell className="text-sm text-slate-900 dark:text-neutral-100">
                  {item.tabName || '-'}
                </TableCell>
              )}
              {visibleColumns.type && (
                <TableCell className="text-sm text-slate-900 dark:text-neutral-100">
                  {item.shared ? 'Shared' : 'Program Specific'}
                </TableCell>
              )}
              {visibleColumns.lastModified && (
                <TableCell className="text-sm text-slate-500 dark:text-neutral-400">
                  {item.updatedAt
                    ? new Date(item.updatedAt).toLocaleDateString()
                    : '-'}
                </TableCell>
              )}
              <TableCell>
                <DotsMenu
                  options={(() => {
                    const userOptions = [];
                    if (checkPermission('update', 'DocumentTemplateProgram')) {
                      userOptions.push({
                        label: 'Edit',
                        onClick: () => handleEdit(item?.id),
                        hoverClass: 'hover:bg-cyan-100',
                      });
                    }
                    if (checkPermission('delete', 'DocumentTemplateProgram')) {
                      userOptions.push({
                        label: 'Delete',
                        onClick: () => handleDelete(item?.id),
                        hoverClass: 'hover:bg-red-100 hover:text-red-800',
                      });
                    }
                    return userOptions;
                  })()}
                />
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>
    </>
  );
};

export default DocumentTemplateProgramTable;
